import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useCustomers } from '../hooks/useCustomers';
import type { Customer, Address } from '../types';
import { geocodeAddress } from '../lib/geocode';

const ITEMS_PER_PAGE = 25;

const FIELD = 'mt-1 w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none';

type NewCustomerForm = {
  first_name: string;
  last_name: string;
  cellphone?: string;
  address_1?: string;
  suburb?: string;
  city?: string;
  postal_code?: string;
};

type EditCustomerForm = {
  first_name: string;
  last_name: string;
  cellphone?: string | null;
};

type NewAddressForm = {
  address_1: string;
  suburb?: string;
  city?: string;
  postal_code?: string;
};

export default function Customers() {
  const {
    customers,
    loading,
    fetchCustomers,
    createCustomer,
    updateCustomer,
    createAddress,
    fetchAddressesForCustomer,
    setCustomersPrimaryAddress,
  } = useCustomers();

  const [query, setQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isAddrOpen, setIsAddrOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [newCustomer, setNewCustomer] = useState<NewCustomerForm>({
    first_name: '', last_name: '', cellphone: '',
    address_1: '', suburb: '', city: '', postal_code: '',
  });
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [editForm, setEditForm] = useState<EditCustomerForm>({
    first_name: '', last_name: '', cellphone: '',
  });
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addrForm, setAddrForm] = useState<NewAddressForm>({
    address_1: '', suburb: '', city: '', postal_code: '',
  });

  useEffect(() => {
    void fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => { setCurrentPage(1); }, [query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c => {
      const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase();
      return name.includes(q) || (c.cellphone?.toLowerCase().includes(q) ?? false);
    });
  }, [customers, query]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCustomers = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };


  const openNew = () => {
    setNewCustomer({ first_name: '', last_name: '', cellphone: '', address_1: '', suburb: '', city: '', postal_code: '' });
    setIsCreateOpen(true);
  };

  const submitNewCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const created = await createCustomer({
        first_name: newCustomer.first_name.trim(),
        last_name:  newCustomer.last_name.trim(),
        cellphone:  newCustomer.cellphone?.trim() || null,
      });
      if (created?.id && newCustomer.address_1?.trim()) {
        const queryLine = [newCustomer.address_1, newCustomer.suburb, newCustomer.city].filter(Boolean).join(', ');
        const coords = await geocodeAddress(queryLine);
        const addr = await createAddress({
          customer_id: String(created.id),
          address_1:   newCustomer.address_1.trim(),
          suburb:      newCustomer.suburb?.trim() || null,
          city:        newCustomer.city?.trim() || null,
          postcode:    newCustomer.postal_code?.trim() || null,
          latitude:    coords ? String(coords.lat) : null,
          longitude:   coords ? String(coords.lng) : null,
        });
        if (addr?.id) await setCustomersPrimaryAddress(String(created.id), String(addr.id));
      }
      if (created) {
        await fetchCustomers();
        setIsCreateOpen(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Edit customer ─────────────────────────────────────────────────────────
  const openEdit = (c: Customer) => {
    setSelectedCustomer(c);
    setEditForm({ first_name: c.first_name ?? '', last_name: c.last_name ?? '', cellphone: c.cellphone ?? '' });
    setIsEditOpen(true);
  };

  const submitEditCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !selectedCustomer) return;
    setSubmitting(true);
    try {
      const updated = await updateCustomer(String(selectedCustomer.id), {
        first_name: editForm.first_name.trim(),
        last_name:  editForm.last_name.trim(),
        cellphone:  (editForm.cellphone ?? '').trim(),
      });
      if (updated) { setIsEditOpen(false); setSelectedCustomer(null); }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Address manager ───────────────────────────────────────────────────────
  const openAddressManager = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setAddrForm({ address_1: '', suburb: '', city: '' });
    const list = await fetchAddressesForCustomer(String(customer.id));
    setAddresses(list);
    setIsAddrOpen(true);
  };

  const submitNewAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !selectedCustomer) return;
    setSubmitting(true);
    try {
      const queryLine = [addrForm.address_1, addrForm.suburb, addrForm.city].filter(Boolean).join(', ');
      const coords = await geocodeAddress(queryLine);
      const created = await createAddress({
        customer_id: String(selectedCustomer.id),
        address_1:   addrForm.address_1.trim(),
        suburb:      addrForm.suburb?.trim() || null,
        city:        addrForm.city?.trim() || null,
        postcode:    addrForm.postal_code?.trim() || null,
        latitude:    coords ? String(coords.lat) : null,
        longitude:   coords ? String(coords.lng) : null,
      });
      if (created) {
        await setCustomersPrimaryAddress(String(selectedCustomer.id), String(created.id));
        const list = await fetchAddressesForCustomer(String(selectedCustomer.id));
        setAddresses(list);
        setAddrForm({ address_1: '', suburb: '', city: '', postal_code: '' });
        await fetchCustomers();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const makePrimary = async (addressId: string) => {
    if (!selectedCustomer) return;
    const ok = await setCustomersPrimaryAddress(String(selectedCustomer.id), addressId);
    if (ok) {
      const list = await fetchAddressesForCustomer(String(selectedCustomer.id));
      setAddresses(list);
      await fetchCustomers();
      setIsAddrOpen(false);
      setSelectedCustomer(null);
    }
  };

  const isPrimary = (a: Address) =>
      selectedCustomer?.delivery_address && String(selectedCustomer.delivery_address) === String(a.id);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
      <div className="space-y-6">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Customers</h1>
            <p className="text-sm text-stone-500 mt-1">Manage customers, contact numbers, and delivery addresses.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
                placeholder="Search name or cellphone…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#ee2b74]"
            />
            <button onClick={openNew}
                    className="rounded-lg px-4 py-2.5 text-sm font-medium text-white bg-[#ee2b74] hover:bg-[#d4245f] transition shadow-sm whitespace-nowrap">
              New Customer
            </button>
          </div>
        </header>

        {/* Table */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
            <h2 className="font-semibold text-stone-900">All Customers</h2>
            <span className="text-sm text-stone-500">{filtered.length} total</span>
          </div>

          {loading ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {['Name', 'Cellphone', 'Primary Address', 'Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                    ))}
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-4 py-3"><div className="h-4 w-36 bg-stone-100 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-28 bg-stone-100 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-48 bg-stone-100 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-7 w-32 bg-stone-100 rounded ml-auto" /></td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          ) : filtered.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-stone-500">No customers found.</div>
          ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Cellphone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Primary Address</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                    {paginatedCustomers.map(c => (
                        <tr key={c.id} className="hover:bg-stone-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-stone-900">
                            {c.first_name ?? ''} {c.last_name ?? ''}
                          </td>
                          <td className="px-4 py-3 text-stone-600">{c.cellphone ?? '—'}</td>
                          <td className="px-4 py-3">
                            {c.primary_address ? (
                                <span className="inline-flex items-center rounded-full border border-stone-200 px-2.5 py-0.5 text-xs text-stone-600">
                                                        {[c.primary_address.address_1, c.primary_address.suburb, c.primary_address.city].filter(Boolean).join(', ')}
                                                    </span>
                            ) : (
                                <span className="text-stone-400 text-xs">None</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2 justify-end flex-wrap">
                              <button onClick={() => openAddressManager(c)}
                                      className="inline-flex items-center rounded-lg px-3 py-1.5 bg-white border border-stone-300 text-xs text-stone-700 hover:bg-stone-50 transition">
                                Manage Addresses
                              </button>
                              <button onClick={() => openEdit(c)}
                                      className="inline-flex items-center rounded-lg px-3 py-1.5 bg-white border border-stone-300 text-xs text-stone-700 hover:bg-stone-50 transition">
                                Edit
                              </button>
                              <Link to={`/customers/${c.id}/orders`}
                                    className="inline-flex items-center rounded-lg px-3 py-1.5 bg-[#ee2b74] hover:bg-[#d4245f] text-white text-xs transition">
                                Orders
                              </Link>
                            </div>
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50/30 px-4 py-3">
                  <p className="text-sm text-stone-600">
                    Showing <span className="font-medium">{totalItems === 0 ? 0 : startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(startIndex + ITEMS_PER_PAGE, totalItems)}</span>{' '}
                    of <span className="font-medium">{totalItems}</span> results
                  </p>
                  <nav className="flex items-center gap-1">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                            className="rounded-lg border border-stone-300 bg-white p-2 shadow-sm transition-colors hover:bg-stone-50 disabled:opacity-40">
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <span className="px-2 text-xs font-medium text-stone-600">Page {currentPage} of {totalPages}</span>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                            className="rounded-lg border border-stone-300 bg-white p-2 shadow-sm transition-colors hover:bg-stone-50 disabled:opacity-40">
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </nav>
                </div>
              </>
          )}
        </section>

        {/* New Customer modal */}
        {isCreateOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
              <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-stone-200 overflow-hidden">
                <div className="h-1 w-full bg-[#ee2b74]" />
                <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900 text-lg">New Customer</h3>
                  <button onClick={() => setIsCreateOpen(false)} className="text-stone-400 hover:text-stone-600 text-xl leading-none">✕</button>
                </div>
                <form onSubmit={submitNewCustomer} className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">First name</label>
                      <input required className={FIELD} value={newCustomer.first_name}
                             onChange={e => setNewCustomer(p => ({ ...p, first_name: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Last name</label>
                      <input required className={FIELD} value={newCustomer.last_name}
                             onChange={e => setNewCustomer(p => ({ ...p, last_name: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Cellphone</label>
                    <input className={FIELD} value={newCustomer.cellphone ?? ''}
                           onChange={e => setNewCustomer(p => ({ ...p, cellphone: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Address line</label>
                    <input className={FIELD} value={newCustomer.address_1 ?? ''}
                           onChange={e => setNewCustomer(p => ({ ...p, address_1: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Suburb</label>
                      <input className={FIELD} value={newCustomer.suburb ?? ''}
                             onChange={e => setNewCustomer(p => ({ ...p, suburb: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">City</label>
                      <input className={FIELD} value={newCustomer.city ?? ''}
                             onChange={e => setNewCustomer(p => ({ ...p, city: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Postal code</label>
                      <input className={FIELD} value={newCustomer.postal_code ?? ''}
                             onChange={e => setNewCustomer(p => ({ ...p, postal_code: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
                    <button type="button" onClick={() => setIsCreateOpen(false)} disabled={submitting}
                            className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                      Cancel
                    </button>
                    <button type="submit" disabled={submitting}
                            className="rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition">
                      {submitting ? 'Creating…' : 'Create Customer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* Edit Customer modal */}
        {isEditOpen && selectedCustomer && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
              <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-stone-200 overflow-hidden">
                <div className="h-1 w-full bg-[#ee2b74]" />
                <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900 text-lg">Edit Customer</h3>
                  <button onClick={() => { setIsEditOpen(false); setSelectedCustomer(null); }}
                          className="text-stone-400 hover:text-stone-600 text-xl leading-none">✕</button>
                </div>
                <form onSubmit={submitEditCustomer} className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">First name</label>
                      <input required className={FIELD} value={editForm.first_name}
                             onChange={e => setEditForm(p => ({ ...p, first_name: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Last name</label>
                      <input required className={FIELD} value={editForm.last_name}
                             onChange={e => setEditForm(p => ({ ...p, last_name: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Cellphone</label>
                    <input className={FIELD} value={editForm.cellphone ?? ''}
                           onChange={e => setEditForm(p => ({ ...p, cellphone: e.target.value }))} />
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
                    <button type="button" onClick={() => { setIsEditOpen(false); setSelectedCustomer(null); }}
                            disabled={submitting}
                            className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                      Cancel
                    </button>
                    <button type="submit" disabled={submitting}
                            className="rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition">
                      {submitting ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* Address manager modal */}
        {isAddrOpen && selectedCustomer && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
              <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl border border-stone-200 overflow-hidden">
                <div className="h-1 w-full bg-[#ee2b74]" />
                <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900 text-lg">
                    Addresses — {selectedCustomer.first_name} {selectedCustomer.last_name}
                  </h3>
                  <button onClick={() => setIsAddrOpen(false)}
                          className="text-stone-400 hover:text-stone-600 text-xl leading-none">✕</button>
                </div>
                <div className="p-5 grid gap-6 md:grid-cols-2">
                  {/* Address list */}
                  <div>
                    <h4 className="text-sm font-semibold text-stone-700 mb-3">Existing Addresses</h4>
                    {addresses.length === 0 ? (
                        <p className="text-sm text-stone-500">No addresses yet.</p>
                    ) : (
                        <ul className="space-y-2 max-h-80 overflow-auto">
                          {addresses.map(a => (
                              <li key={a.id} className="rounded-xl border border-stone-200 p-3">
                                <div className="text-sm text-stone-800">
                                  {a.address_1}{a.suburb ? `, ${a.suburb}` : ''}{a.city ? `, ${a.city}` : ''}
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                                    <span className={`text-xs font-medium ${isPrimary(a) ? 'text-[#ee2b74]' : 'text-stone-400'}`}>
                                                        {isPrimary(a) ? 'Primary' : '—'}
                                                    </span>
                                  {!isPrimary(a) && (
                                      <button onClick={() => makePrimary(String(a.id))}
                                              className="text-xs rounded-full border border-stone-300 px-2.5 py-1 hover:bg-stone-100 transition">
                                        Set as Primary
                                      </button>
                                  )}
                                </div>
                              </li>
                          ))}
                        </ul>
                    )}
                  </div>

                  {/* Add new address */}
                  <form onSubmit={submitNewAddress} className="space-y-3">
                    <h4 className="text-sm font-semibold text-stone-700 mb-3">Add Address</h4>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Address line</label>
                      <input required className={FIELD} value={addrForm.address_1}
                             onChange={e => setAddrForm(p => ({ ...p, address_1: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1.5">Suburb</label>
                        <input className={FIELD} value={addrForm.suburb ?? ''}
                               onChange={e => setAddrForm(p => ({ ...p, suburb: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1.5">City</label>
                        <input className={FIELD} value={addrForm.city ?? ''}
                               onChange={e => setAddrForm(p => ({ ...p, city: e.target.value }))} />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setIsAddrOpen(false)} disabled={submitting}
                              className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                        Close
                      </button>
                      <button type="submit" disabled={submitting}
                              className="rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition">
                        {submitting ? 'Adding…' : 'Add Address'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
        )}
      </div>
  );
}